import React, { useMemo } from 'react';

import {
    formatPurchaseOrderDate,
    formatPurchaseOrderDateTime,
} from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDateUtils';
import {
    FALLBACK_TEXT,
    formatBuyerNames,
    formatCurrency,
    formatDeliveryType,
    formatTemperatureRange,
    normalizeOptionalText,
} from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDisplayUtils';
import { Badge } from 'shadcn/components/ui/Badge';

/**
 * Read-only summary of purchase order header fields.
 *
 * @param {Object} props
 * @param {Object|null} [props.purchaseOrder=null] PO payload from the API
 */
export function PurchaseOrderPrimaryInfoCard({ purchaseOrder = null }) {
    const resolvedPurchaseOrder = purchaseOrder ?? null;

    const {
        buyers,
        confirmed,
        createdAt,
        crossDockDeliveryDate,
        crossDockName,
        deliveryType,
        department,
        estimatedFreightCost,
        facilityName,
        id,
        maxTempReq,
        minTempReq,
        pickupDate,
        poTotalCost,
        produceOrGrocery,
        scheduledDeliveryDate,
        sentToVendor,
        shipperID,
        shipperName,
        specialInstructions,
        status,
        truckID,
        vendorName,
    } = resolvedPurchaseOrder ?? {};

    const infoSections = useMemo(
        () => [
            {
                label: 'PO #',
                value: normalizeOptionalText(id),
            },
            {
                label: 'Status',
                value: normalizeOptionalText(status),
            },
            {
                label: 'Created On',
                value: formatPurchaseOrderDateTime(createdAt, FALLBACK_TEXT),
            },
            {
                label: 'Confirmed At',
                value: formatPurchaseOrderDateTime(confirmed, FALLBACK_TEXT),
                alwaysShow: true,
            },
            {
                label: 'Facility',
                value: normalizeOptionalText(facilityName),
            },
            {
                label: 'Buyer(s)',
                value: formatBuyerNames(buyers),
            },
            {
                label: 'Produce/Grocery',
                value: normalizeOptionalText(produceOrGrocery),
            },
            {
                label: 'Delivery Type',
                value: formatDeliveryType(deliveryType),
                render: (value) => (
                    <Badge variant="secondary" className="h-6 justify-start px-2 py-0 text-xs uppercase tracking-wide">
                        {value}
                    </Badge>
                ),
            },
            {
                label: 'Vendor',
                value: normalizeOptionalText(vendorName),
            },
            {
                label: 'Shipper',
                value: normalizeOptionalText(
                    shipperName === 'Unknown' || shipperName === 'unknown' ? null : shipperName,
                ),
            },
            {
                label: 'Pick Up Date',
                value: formatPurchaseOrderDate(pickupDate, FALLBACK_TEXT),
            },
            {
                label: 'Scheduled Delivery',
                value: formatPurchaseOrderDateTime(scheduledDeliveryDate, FALLBACK_TEXT),
            },
            {
                label: 'Temperature (Min - Max)',
                value: formatTemperatureRange(minTempReq, maxTempReq),
            },
            {
                label: 'Cross Dock',
                value: normalizeOptionalText(crossDockName),
            },
            {
                label: 'Cross Dock Delivery Date',
                value: formatPurchaseOrderDate(crossDockDeliveryDate, FALLBACK_TEXT),
            },
            {
                label: 'Truck ID',
                value: normalizeOptionalText(truckID),
            },
            {
                label: 'Freight Cost',
                value: formatCurrency(estimatedFreightCost),
            },
            {
                label: 'Total Cost',
                value: formatCurrency(poTotalCost),
            },
            {
                label: 'Special Instructions',
                value: normalizeOptionalText(specialInstructions),
            },
        ],
        [
            buyers,
            confirmed,
            createdAt,
            crossDockDeliveryDate,
            crossDockName,
            deliveryType,
            department,
            estimatedFreightCost,
            facilityName,
            id,
            maxTempReq,
            minTempReq,
            pickupDate,
            poTotalCost,
            produceOrGrocery,
            scheduledDeliveryDate,
            sentToVendor,
            shipperID,
            shipperName,
            specialInstructions,
            status,
            truckID,
            vendorName,
        ],
    );

    const visibleSections = useMemo(
        () =>
            infoSections.filter(({ alwaysShow, value }) => alwaysShow || (value !== FALLBACK_TEXT && value !== 'N/A')),
        [infoSections],
    );

    return (
        <div className="rounded-md border bg-card p-4 shadow-sm">
            <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1">
                    <h2 className="text-left text-base font-semibold tracking-tight text-foreground">
                        Purchase Order Information
                    </h2>
                </div>

                <dl className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
                    {visibleSections.map(({ label, render, value }) => (
                        <div key={label} className="flex flex-col gap-0.5">
                            <dt className="text-left text-xs font-semibold uppercase leading-tight tracking-wide text-muted-foreground">
                                {label}
                            </dt>
                            <dd className="text-left text-sm font-medium text-foreground">
                                {value !== FALLBACK_TEXT && typeof render === 'function' ? render(value) : value}
                            </dd>
                        </div>
                    ))}
                </dl>
            </div>
        </div>
    );
}

PurchaseOrderPrimaryInfoCard.displayName = 'PurchaseOrderPrimaryInfoCard';
